import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { buildUrlWithParams } from '../../utils';

const DEFAULT_FEATURED_CARDS_ENDPOINT = '/api/votecardsajax_hc4.ip-minimal.php';
const DEFAULT_CARD_VOTE_ENDPOINT = '/api/card/vote.php';

const normalizeExtension = (extension) =>
  String(extension || 'png').replace(/^\./, '');

const getCardImageUrl = (id, extension = 'png') => {
  if (!id) return '';

  const value = String(id);
  if (/^(?:https?:|blob:|data:|\/)/i.test(value)) return value;

  return `/cards/${value}.${normalizeExtension(extension)}`;
};

const normalizeTokens = (tokens, fallbackCard) => {
  let value = tokens;

  if (typeof value === 'string' && value !== '0') {
    try {
      value = JSON.parse(value);
    } catch {
      value = [];
    }
  }

  if (!Array.isArray(value)) return [];

  return value
    .filter((token) => token?.id)
    .map((token, index) => ({
      id: token.id,
      extension: normalizeExtension(token.ext || fallbackCard.ext),
      style: token.style || fallbackCard.style || 'minion',
      label: token.title || token.name || `Token ${index + 1}`
    }));
};

const normalizeFeaturedCards = (payload) => {
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.cards)
      ? payload.cards
      : [];

  return source
    .filter((card) => card?.id && card.cid != null && String(card.cid) !== '0')
    .map((card) => ({
      ratingId: card.cid,
      id: card.id,
      extension: normalizeExtension(card.ext),
      style: card.style || 'minion',
      title: card.title || card.name || 'Community card',
      tokens: normalizeTokens(card.tokens, card)
    }));
};

const getCardImageStyle = (cardStyle = '') => {
  const normalizedStyle = String(cardStyle).toLowerCase();
  let width = 411;
  let top = 16;

  if (normalizedStyle === 'spell-legendary') width = 433;
  if (normalizedStyle.startsWith('weapon')) {
    width = 412;
    top = 32;
  }

  return {
    position: 'absolute',
    top,
    left: '50%',
    width,
    height: 'auto',
    transform: 'translateX(-50%)',
    userSelect: 'none',
    WebkitUserDrag: 'none'
  };
};

export default function FeaturedCardReview({
  enabled = true,
  cardsEndpoint = DEFAULT_FEATURED_CARDS_ENDPOINT,
  voteEndpoint = DEFAULT_CARD_VOTE_ENDPOINT,
  onAvailabilityChange,
  onComplete,
  showToast
}) {
  const [cards, setCards] = useState([]);
  const [cardIndex, setCardIndex] = useState(0);
  const [assetIndex, setAssetIndex] = useState(0);
  const [status, setStatus] = useState('idle');
  const [hoveredRating, setHoveredRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setStatus('idle');
      return undefined;
    }

    const controller = new AbortController();

    const loadCards = async () => {
      setStatus('loading');

      try {
        const response = await fetch(cardsEndpoint, {
          signal: controller.signal,
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) {
          throw new Error(`Featured cards request failed (${response.status}).`);
        }

        const nextCards = normalizeFeaturedCards(await response.json());

        setCards(nextCards);
        setCardIndex(0);
        setAssetIndex(0);
        setStatus(nextCards.length ? 'ready' : 'empty');
      } catch (error) {
        if (error.name === 'AbortError') return;

        // This feature is optional. A failed review request must not block editing.
        setCards([]);
        setStatus('error');
      }
    };

    loadCards();
    return () => controller.abort();
  }, [cardsEndpoint, enabled]);

  const activeCard = cards[cardIndex] || null;

  const assets = useMemo(() => {
    if (!activeCard) return [];

    return [
      {
        id: activeCard.id,
        extension: activeCard.extension,
        style: activeCard.style,
        label: activeCard.title,
        isMainCard: true
      },
      ...activeCard.tokens.map((token) => ({
        ...token,
        isMainCard: false
      }))
    ];
  }, [activeCard]);

  const activeAsset = assets[assetIndex] || null;
  const isVisible = Boolean(
    enabled &&
    status === 'ready' &&
    activeCard &&
    activeAsset
  );

  useEffect(() => {
    onAvailabilityChange?.(isVisible);
  }, [isVisible, onAvailabilityChange]);

  useEffect(() => {
    return () => onAvailabilityChange?.(false);
  }, [onAvailabilityChange]);

  useEffect(() => {
    if (!isVisible) return undefined;

    const urls = [
      ...assets.map((asset) => getCardImageUrl(asset.id, asset.extension)),
      cards[cardIndex + 1]
        ? getCardImageUrl(cards[cardIndex + 1].id, cards[cardIndex + 1].extension)
        : ''
    ].filter(Boolean);

    const images = urls.map((url) => {
      const image = new Image();
      image.src = url;
      return image;
    });

    return () => {
      images.forEach((image) => {
        image.onload = null;
        image.onerror = null;
      });
    };
  }, [assets, cardIndex, cards, isVisible]);

  const cycleAsset = useCallback(() => {
    if (assets.length <= 1) return;
    setAssetIndex((current) => (current + 1) % assets.length);
  }, [assets.length]);

  const finishReview = useCallback(() => {
    setStatus('complete');
    onComplete?.();
    showToast?.('Thanks for rating the community cards.');
  }, [onComplete, showToast]);

  const advanceCard = useCallback(() => {
    const nextIndex = cardIndex + 1;

    if (nextIndex >= cards.length) {
      finishReview();
      return;
    }

    setAssetIndex(0);
    setHoveredRating(0);
    setCardIndex(nextIndex);
  }, [cardIndex, cards.length, finishReview]);

  const submitRating = useCallback(async (rating) => {
    if (!activeCard?.ratingId || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(voteEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: activeCard.ratingId,
          rate: rating
        })
      });

      if (!response.ok) {
        throw new Error(`Vote request failed (${response.status}).`);
      }

      advanceCard();
    } catch (error) {
      showToast?.('Could not save your rating. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [activeCard, advanceCard, isSubmitting, showToast, voteEndpoint]);

  const handleImageError = useCallback(() => {
    setStatus('error');
    showToast?.('That community card image could not be loaded. Opening the editor instead.');
    onComplete?.();
  }, [onComplete, showToast]);

  if (!isVisible) return null;

  return (
    <>
      <div
        data-featured-card-review
        aria-label="Community card review"
        style={{
          position: 'absolute',
          top: 53,
          left: 668,
          width: 430,
          height: 590,
          zIndex: 11,
          filter: 'drop-shadow(0px 0px 15px rgba(0,0,0,0.7))'
        }}
      >
<Link
  to={buildUrlWithParams({
    ...Object.fromEntries(
      new URLSearchParams(window.location.search)
    ),
    selectedCard: activeCard.ratingId
  })}
  aria-label={`Open ${activeCard.title}`}
  title="Open this card"
  style={{ display: 'block' }}
>
  <img
    key={`${activeAsset.id}-${assetIndex}`}
    src={getCardImageUrl(
      activeAsset.id,
      activeAsset.extension
    )}
    alt={activeAsset.label || 'Community card'}
    onError={handleImageError}
    style={{
      ...getCardImageStyle(activeAsset.style),
      cursor: 'pointer'
    }}
    draggable={false}
  />
</Link>

        {assets.length > 1 && (
          <button
            type="button"
            onClick={cycleAsset}
            aria-label={`Show next attached card. Currently showing ${assetIndex + 1} of ${assets.length}.`}
            title={`Cycle main card and ${assets.length - 1} token${assets.length === 2 ? '' : 's'}`}
            style={{
              position: 'absolute',
              top: 84,
              right: -3,
              width: 68,
              height: 65,
              padding: '5px 4px 0',
              border: 0,
              background: 'url(/images/more_cards_icon.png) center / contain no-repeat',
              color: '#fff',
              cursor: 'pointer',
              fontFamily: 'Belwe, serif',
              fontSize: 20,
              lineHeight: 1,
              textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
            }}
          >
            <span style={{ position: 'absolute', width: '100%', left: -2, top: 18 }}>{assetIndex + 1}/{assets.length}</span>
          </button>
        )}
      </div>

      <div
        data-featured-card-review
        style={{
          position: 'absolute',
          top: 643,
          left: 818,
          minHeight: 52,
          zIndex: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0,
          padding: '0px 0px 0px'
                }}
      >

        {[1, 2, 3, 4, 5].map((rating) => {
          const highlighted = rating <= hoveredRating;

          return (
<button
  key={rating}
  type="button"
  disabled={isSubmitting}
  aria-label={`Rate ${rating} star${rating === 1 ? '' : 's'}`}
  onMouseEnter={() => setHoveredRating(rating)}
  onMouseLeave={() => setHoveredRating(0)}
  onFocus={() => setHoveredRating(rating)}
  onBlur={() => setHoveredRating(0)}
  onClick={() => submitRating(rating)}
  style={{
    width: 28,
    height: 30,
    padding: 0,
    border: 0,
    background: 'transparent',
    cursor: isSubmitting ? 'wait' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}
>
  <svg
    viewBox="0 0 24 24"
    style={{
      width: '100%',
      height: '100%',

    }}
  >
    <path
      d="M12 .587l3.668 7.431 8.2 1.192-5.934 5.787 1.4 8.168L12 18.896l-7.334 3.857 1.4-8.168L.132 9.21l8.2-1.192z"
      fill={highlighted ? '#ffc04a' : '#361c0a'} // Changes fill on highlight
      stroke="#7e6536"
      strokeWidth="1"
    />
  </svg>
</button>
          );
        })}

      </div>
    </>
  );
}
